import { useMemo, useState } from 'react';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { Alert } from 'lib/components/Alert';
import { ColumnService } from 'lib/services/directory';
import { TableLayout } from 'lib/components/TableLayout';
import { Badge } from 'lib/components/Badge';
import { formatPriceWithCentsAndCommas } from 'lib/pricing/ui';
import Card from 'lib/components/Card/Card';
import { CustomersTableRow } from 'lib/types/reports/CustomerTableRows';
import {
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Popover } from 'lib/components/Popover';
import { Permissions } from 'lib/permissions/roles';
import { Tooltip } from 'lib/components/Tooltip/Tooltip';
import { useReportParams } from '../context/ReportParamsContext';
import { useReportsLoading } from '../context/LoadingContext';
import { ReportHeader } from '../components/ReportHeader';
import { ReportsProductFilter } from '../components/ProductFilter';
import { REPORT_CURRENCY_SYMBOL } from '../constants/csvColumns';
import { fetchCustomerNoticesAndOrdersCohorts } from '../utils/elastic';

enum SortBy {
  Revenue = 'revenue',
  OrderCount = 'orderCount',
  None = 'none'
}

enum SortDirection {
  Asc = 'asc',
  Desc = 'desc'
}

export function CustomersTab() {
  const { getOrgIdsWithPermission, getOrgIds, getDateRange } =
    useReportParams();
  const { dateRange, startDate, endDate } = getDateRange();
  const { withLoading } = useReportsLoading();

  const orgIds = getOrgIds();
  const allowedOrgIds = getOrgIdsWithPermission(Permissions.PAYMENTS_REPORT);

  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.None);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.Desc
  );

  const {
    value: fetchedData,
    isLoading,
    error
  } = useAsyncEffect<CustomersTableRow[] | null>({
    fetchData: async () =>
      withLoading(
        fetchCustomerNoticesAndOrdersCohorts(allowedOrgIds, getDateRange)
      ),
    initialData: null,
    dependencies: [orgIds.join(','), dateRange, startDate, endDate],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message:
        '[COLUMN REPORTS] Failed to fetch customer data for Customers tab',
      tags: {
        orgsFilter: orgIds.join(', '),
        dateRange,
        startDateFilter: startDate || '',
        endDateFilter: endDate || ''
      }
    }
  });

  const rows: CustomersTableRow[] = useMemo(() => {
    if (!fetchedData) {
      return [];
    }

    let processedRows: CustomersTableRow[] = fetchedData;

    if (selectedProducts.length > 0) {
      processedRows = fetchedData.filter(row =>
        row.products.some(product => selectedProducts.includes(product))
      );
    }

    if (sortBy !== SortBy.None) {
      processedRows.sort((a, b) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === SortDirection.Asc
            ? aValue - bValue
            : bValue - aValue;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = (aValue as string).localeCompare(bValue as string);
          return sortDirection === SortDirection.Asc ? comparison : -comparison;
        }

        return 0;
      });
    }

    return processedRows;
  }, [fetchedData, selectedProducts, sortBy, sortDirection]);

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setSortDirection(
        sortDirection === SortDirection.Asc
          ? SortDirection.Desc
          : SortDirection.Asc
      );
    } else {
      setSortBy(column);
      setSortDirection(SortDirection.Desc);
    }
  };

  const headerComponents = {
    revenue: (
      <th
        key={SortBy.Revenue}
        className="font-medium cursor-pointer select-none"
        onClick={() => handleSort(SortBy.Revenue)}
        style={{ userSelect: 'none' }}
      >
        <div className="flex items-center gap-1 font-medium">
          Revenue
          {sortBy === SortBy.Revenue &&
            sortDirection === SortDirection.Desc && (
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            )}
          {sortBy === SortBy.Revenue && sortDirection === SortDirection.Asc && (
            <ChevronUpIcon className="w-4 h-4 ml-1" />
          )}
          {sortBy !== SortBy.Revenue && (
            <ChevronDownIcon className="w-4 h-4 ml-1 text-column-gray-300" />
          )}
        </div>
      </th>
    ),
    orderCount: (
      <th
        key={SortBy.OrderCount}
        className="font-medium cursor-pointer select-none"
        onClick={() => handleSort(SortBy.OrderCount)}
        style={{ userSelect: 'none' }}
      >
        <div className="flex items-center gap-1 font-medium">
          Orders
          {sortBy === SortBy.OrderCount &&
            sortDirection === SortDirection.Desc && (
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            )}
          {sortBy === SortBy.OrderCount &&
            sortDirection === SortDirection.Asc && (
              <ChevronUpIcon className="w-4 h-4 ml-1" />
            )}
          {sortBy !== SortBy.OrderCount && (
            <ChevronDownIcon className="w-4 h-4 ml-1 text-column-gray-300" />
          )}
        </div>
      </th>
    ),
    products: (
      <th key="products" className="font-medium">
        <Popover
          id="products-filter-popover"
          activator={
            <button
              type="button"
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-column-gray-50 border border-transparent focus:outline-none"
              aria-label="Filter by Products"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">PRODUCTS</span>
                {selectedProducts.length > 0 && (
                  <Badge status="info" size="sm">
                    {selectedProducts.length}
                  </Badge>
                )}
                <FunnelIcon className="w-4 h-4 ml-1" />
              </div>
            </button>
          }
          alignment="right"
          fullWidth
        >
          <div className="p-2 w-[1100px]">
            <ReportsProductFilter
              value={selectedProducts}
              onChange={setSelectedProducts}
            />
          </div>
        </Popover>
      </th>
    )
  };

  const columns = [
    'Customer',
    'Email',
    headerComponents.revenue,
    headerComponents.orderCount,
    headerComponents.products
  ];

  return (
    <Card>
      <ReportHeader
        title={'Customers'}
        subtitle={
          'Revenue is based on orders with a last publication date in the selected range, excluding Column service fees, and grouped by customer. Sort by revenue or order volume to explore performance.'
        }
        permission={Permissions.PAYMENTS_REPORT}
      />
      <div className="p-6">
        {error && (
          <Alert status="error" id="customers-tab-error">
            Error fetching customer data: {error.message}
          </Alert>
        )}
        <TableLayout
          columns={columns}
          data={rows}
          id="customers-table"
          loading={isLoading}
          header={{
            title: '',
            subtitle: ''
          }}
          filterable={{
            shouldShowTableItem: (item: CustomersTableRow, search: string) =>
              item.customer.toLowerCase().includes(search.toLowerCase()) ||
              item.email.toLowerCase().includes(search.toLowerCase()),
            searchEnabled: true
          }}
          renderRow={(row: CustomersTableRow) => (
            <>
              <td>
                <Tooltip
                  helpText={`${row.isOrg ? 'Org' : 'Customer'} id: ${row.key}`}
                >
                  <span>{row.customer}</span>
                </Tooltip>
              </td>
              <td>
                <span>{row.email}</span>
              </td>
              <td>
                {formatPriceWithCentsAndCommas(
                  REPORT_CURRENCY_SYMBOL,
                  row.revenue
                )}
              </td>
              <td>{row.orderCount}</td>
              <td>
                <div style={{ minWidth: '160px' }}>
                  {row.products.map((product: string) => (
                    <Badge key={product}>{product}</Badge>
                  ))}
                </div>
              </td>
            </>
          )}
        />
      </div>
    </Card>
  );
}
